import React from 'react';
import Skeleton from 'react-loading-skeleton';
import BenefitsCarousel from './benefits/BenefitsCarousel';
import UpcomingPaymentsChart from './UpcomingPaymentsChart';
import { formatCurrency } from '../utils/formatCurrency';
import { formatDate } from '../utils/dateFormatter';
import { normalizePaymentPlanSummary, hasDelinquentPaymentPlans } from '../utils/paymentPlanDisplay';
import 'react-loading-skeleton/dist/skeleton.css';

function AccountStatusSkeleton() {
    return (
        <>
            {/* Header Skeleton */}
            <div className="card mb-4">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center">
                        <div className="icon-circle bg-light">
                            <Skeleton circle width={40} height={40} />
                        </div>
                        <div className="ms-3">
                            <Skeleton width={200} height={24} />
                            <Skeleton width={120} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Estado de Cuenta Skeleton */}
            <div className="card mb-4">
                <div className="card-body p-4">
                    <Skeleton width={150} className="mb-3" />
                    
                    <div className="p-3 bg-light rounded-3 mb-3">
                        <div className="d-flex align-items-center">
                            <div className="icon-circle bg-light">
                                <Skeleton circle width={40} height={40} />
                            </div>
                            <div className="ms-3 flex-grow-1">
                                <Skeleton width={180} height={28} />
                                <Skeleton width={220} className="mt-2" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Próximos Vencimientos Skeleton */}
            <div className="card">
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <Skeleton width={150} height={20} className="mb-1" />
                            <Skeleton width={30} height={20} />
                        </div>
                    </div>
                    <div className="d-flex gap-2 mb-2">
                        <Skeleton width={40} height={16} />
                        <Skeleton width={40} height={16} />
                        <Skeleton width={40} height={16} />
                    </div>
                    <div className="d-flex gap-2 mb-3" style={{ height: '120px', alignItems: 'flex-end' }}>
                        <Skeleton height="100%" style={{ flex: 1, borderRadius: '12px 12px 0 0' }} />
                        <Skeleton height="60%" style={{ flex: 1, borderRadius: '12px 12px 0 0' }} />
                        <Skeleton height="50%" style={{ flex: 1, borderRadius: '12px 12px 0 0' }} />
                    </div>
                    <div className="d-flex gap-2">
                        <Skeleton width="100%" height={24} style={{ flex: 1 }} />
                        <Skeleton width="100%" height={24} style={{ flex: 1 }} />
                        <Skeleton width="100%" height={24} style={{ flex: 1 }} />
                    </div>
                    <div className="text-center mt-2">
                        <Skeleton width={120} height={14} />
                    </div>
                </div>
            </div>
        </>
    );
}

function Home({ accountInfo, client, user, onNavigate }) {
    if (!accountInfo) return <AccountStatusSkeleton />;

    const { customer, financial, next_installments, installments_by_period, payment_plan_summary } = accountInfo;
    const schedule = installments_by_period?.length ? installments_by_period : (next_installments || []);
    const planSummary = normalizePaymentPlanSummary(payment_plan_summary);
    const hasActivePlan = planSummary.has_active_payment_plan;
    const hasDelinquentPlan = hasDelinquentPaymentPlans(planSummary);
    const hasBrokenPlan = (planSummary.broken_payment_plans_count ?? 0) > 0;
    const planPendingAmount = parseFloat(planSummary.total_payment_plan_pending || 0);

    const hasOverdueAmount = financial.overdue_amount > 0;
    const hasCurrentAmount = financial.current_amount > 0;

    const getNextDueSubtitle = () => {
        if (hasActivePlan && planSummary.next_payment_plan_due_date) {
            const dateLabel = formatDate(planSummary.next_payment_plan_due_date);
            const amountLabel = planSummary.next_payment_plan_due_amount
                ? ` — ${formatCurrency(planSummary.next_payment_plan_due_amount)}`
                : '';

            return `Próximo vencimiento: ${dateLabel}${amountLabel}`;
        }

        const nextInstallment = schedule.find((item) => {
            const isFuture = !item?.bucket || item.bucket === 'future';
            return isFuture && Number(item?.amount) > 0;
        }) || schedule.find((item) => Number(item?.amount) > 0);
        if (!nextInstallment) {
            return null;
        }

        const whenLabel = nextInstallment.period
            || (nextInstallment.due_date ? formatDate(nextInstallment.due_date) : null);

        if (!whenLabel) {
            return null;
        }

        const amountLabel = nextInstallment.amount
            ? ` — ${formatCurrency(nextInstallment.amount)}`
            : '';

        return `Próximo vencimiento: ${whenLabel}${amountLabel}`;
    };

    const getStatusAlert = () => {
        if (hasOverdueAmount) {
            return {
                alertClass: 'alert-danger',
                icon: 'bi-exclamation-triangle',
                title: 'Deuda vencida',
                highlight: formatCurrency(financial.overdue_amount),
                detail: `Deuda total: ${formatCurrency(financial.total_debt)}`,
            };
        }

        if (hasDelinquentPlan) {
            const nextDueDate = planSummary.next_payment_plan_due_date
                ? formatDate(planSummary.next_payment_plan_due_date)
                : null;
            const nextDueAmount = planSummary.next_payment_plan_due_amount || null;

            return {
                alertClass: 'alert-warning',
                icon: 'bi-exclamation-triangle',
                title: 'Plan de pagos en mora',
                highlight: nextDueAmount
                    ? formatCurrency(nextDueAmount)
                    : 'Regularizá tu plan',
                subtitle: nextDueDate ? `Vencimiento: ${nextDueDate}` : getNextDueSubtitle(),
                detail: planSummary.delinquent_payment_plans_count === 1
                    ? 'Tenés una cuota vencida del plan. Pagala pronto para evitar que el plan caiga y vuelva la deuda original.'
                    : `Tenés ${planSummary.delinquent_payment_plans_count} planes con cuotas vencidas. Regularizalos para evitar que caigan.`,
            };
        }

        if (hasActivePlan && planPendingAmount > 0) {
            const nextDueDate = planSummary.next_payment_plan_due_date
                ? formatDate(planSummary.next_payment_plan_due_date)
                : null;
            const nextDueAmount = planSummary.next_payment_plan_due_amount || null;

            return {
                alertClass: 'alert-primary',
                icon: 'bi-calendar-check',
                title: 'Próximo vencimiento',
                highlight: nextDueAmount
                    ? formatCurrency(nextDueAmount)
                    : nextDueDate,
                detail: [
                    nextDueDate && nextDueAmount ? `Vence el ${nextDueDate}` : null,
                    `Pendiente del plan: ${formatCurrency(planSummary.total_payment_plan_pending)}`,
                ].filter(Boolean).join(' · '),
            };
        }

        if (hasBrokenPlan) {
            return {
                alertClass: 'alert-warning',
                icon: 'bi-exclamation-triangle',
                title: 'Plan de pagos caído',
                highlight: 'Revisá tus préstamos',
                subtitle: getNextDueSubtitle(),
                detail: planSummary.broken_payment_plans_count === 1
                    ? 'Un préstamo volvió al cronograma regular de cuotas.'
                    : `${planSummary.broken_payment_plans_count} préstamos volvieron al cronograma regular de cuotas.`,
            };
        }

        if (hasCurrentAmount) {
            const currentObligation = schedule.find(
                (item) => item?.bucket === 'current' && Number(item?.amount) > 0
            );
            const dueDetail = currentObligation?.due_date
                ? `Vence el ${formatDate(currentObligation.due_date)}`
                : (currentObligation?.period
                    ? `Vence en ${currentObligation.period}`
                    : (next_installments?.[0] ? `Vence en ${next_installments[0].period}` : null));

            return {
                alertClass: 'alert-warning',
                icon: 'bi-calendar-event',
                title: 'A pagar este mes',
                highlight: formatCurrency(financial.current_amount),
                detail: dueDetail,
            };
        }

        return {
            alertClass: 'alert-success',
            icon: 'bi-check-circle',
            title: '¡Felicitaciones!',
            highlight: 'Estás al día',
            subtitle: getNextDueSubtitle(),
            detail: hasActivePlan
                ? 'No tenés compromisos pendientes del plan por ahora.'
                : '',
        };
    };

    const status = getStatusAlert();

    const handleGoToLoans = () => {
        if (onNavigate) {
            onNavigate('loans');
        }
    };

    return (
        <>
            {/* Header con info del cliente */}
            <div className="card mb-4">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center">
                        <div className="icon-circle bg-primary-subtle">
                            <i className="bi-person-fill text-primary icon-lg"></i>
                        </div>
                        <div className="ms-3">
                            <h5 className="mb-1">{ customer.name ? customer.name : user.first_name + ' ' + user.last_name }</h5>
                            <div className="text-muted small">
                                DNI: {customer.identification.dni}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Estado de cuenta */}
            <h6 className="text-muted mb-3 p-4 pb-0">Estado de Cuenta</h6>
            <div className="card mb-4">
                <div className="card-body p-4">
                    {hasActivePlan && (
                        <button
                            type="button"
                            className={`btn rounded-pill w-100 mb-3 d-flex align-items-center justify-content-between py-2 px-3 ${
                                hasDelinquentPlan ? 'btn-warning' : 'btn-primary'
                            }`}
                            onClick={handleGoToLoans}
                        >
                            <span className="d-flex align-items-center">
                                <i className={`bi ${hasDelinquentPlan ? 'bi-exclamation-triangle' : 'bi-calendar-check'} me-2`}></i>
                                {hasDelinquentPlan
                                    ? 'Tenés planes de pago en mora'
                                    : 'Tenés planes de pago vigentes'}
                            </span>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    )}

                    <div className={`alert ${status.alertClass} border-0 shadow-sm mb-0`} role="alert">
                        <div className="d-flex align-items-start gap-2">
                            <i className={`bi ${status.icon} fs-5`}></i>
                            <div>
                                <div className="fw-semibold mb-1">{status.title}</div>
                                {status.highlight && (
                                    <div className="fs-5 fw-semibold mb-1">{status.highlight}</div>
                                )}
                                {status.subtitle && (
                                    <div className="small mb-1">{status.subtitle}</div>
                                )}
                                {status.detail && (
                                    <div className="small mb-0">{status.detail}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {hasBrokenPlan && hasActivePlan && (
                        <div className="alert alert-warning border-0 shadow-sm mb-0 mt-3" role="alert">
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-exclamation-triangle fs-5"></i>
                                <div className="small mb-0">
                                    {planSummary.broken_payment_plans_count === 1
                                        ? 'También tenés un préstamo con plan de pagos caído.'
                                        : `También tenés ${planSummary.broken_payment_plans_count} préstamos con plan de pagos caído.`}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Beneficios Exclusivos (solo para demo) */}
            {client?.domain === 'demo' && <BenefitsCarousel />}

            {/* Próximos Vencimientos */}
            {schedule.length > 0 && (
                <div className="card mb-4">
                    <div className="card-body p-4">
                        <UpcomingPaymentsChart data={schedule} />
                    </div>
                </div>
            )}
        </>
    );
}

export default Home;
